<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { fetchKeyStatus, KeyRejectedError, saveApiKey, type KeyStatus } from '../lib/apiKey';

const emit = defineEmits<{ saved: [] }>();

const status = ref<KeyStatus | null>(null);
const statusError = ref<string | null>(null);
const apiKey = ref('');
const saving = ref(false);
const saved = ref(false);
const saveError = ref<string | null>(null);

async function loadStatus(): Promise<void> {
  statusError.value = null;
  try {
    status.value = await fetchKeyStatus();
  } catch (error) {
    statusError.value = error instanceof Error ? error.message : String(error);
  }
}

async function submit(): Promise<void> {
  const value = apiKey.value.trim();
  if (!value || saving.value) {
    return;
  }

  saving.value = true;
  saved.value = false;
  saveError.value = null;

  try {
    await saveApiKey(value);
    // Never keep the key in memory once it is stored.
    apiKey.value = '';
    saved.value = true;
    await loadStatus();
    // Tell the parent so the chat unblocks, but stay on this screen: navigating
    // away here would hide the confirmation the person is waiting for.
    emit('saved');
  } catch (error) {
    saveError.value =
      error instanceof KeyRejectedError || error instanceof Error
        ? error.message
        : String(error);
  } finally {
    saving.value = false;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' });
}

onMounted(loadStatus);
</script>

<template>
  <div class="settings">
    <h1>MyParcel API key</h1>
    <p class="settings__lead">
      De assistent gebruikt jouw MyParcel API key om zendingen aan te maken en labels op te
      halen. De key wordt versleuteld opgeslagen en komt nooit terug naar je browser.
    </p>

    <div v-if="statusError" class="notice notice--error">{{ statusError }}</div>

    <div v-else-if="status?.configured" class="notice notice--ok">
      <p>
        Er staat een API key ingesteld<span v-if="status.lastModified">, laatst gewijzigd op
        {{ formatDate(status.lastModified) }}</span>. Je kunt hieronder een nieuwe invullen om
        hem te vervangen.
      </p>
    </div>

    <div v-else-if="status" class="notice notice--warn">
      <p>Er staat nog geen API key ingesteld. Vul hem hieronder in.</p>
    </div>

    <form class="settings__form" @submit.prevent="submit">
      <label for="api-key">API key</label>
      <input
        id="api-key"
        v-model="apiKey"
        type="password"
        autocomplete="off"
        spellcheck="false"
        placeholder="Plak hier je MyParcel API key"
      />
      <button class="btn btn--primary" type="submit" :disabled="saving || apiKey.trim().length === 0">
        {{ saving ? 'Valideren…' : 'Opslaan en valideren' }}
      </button>
    </form>

    <p v-if="saved" class="notice notice--ok">
      De key is gevalideerd bij MyParcel en opgeslagen.
    </p>
    <p v-if="saveError" class="notice notice--error">{{ saveError }}</p>

    <details class="settings__help">
      <summary>Waar vind ik mijn API key?</summary>
      <ol>
        <li>Log in op <a href="https://backoffice.myparcel.nl" target="_blank" rel="noopener">MyParcel Backoffice</a></li>
        <li>Ga naar <strong>Instellingen → API</strong></li>
        <li>Genereer of kopieer je API key</li>
      </ol>
      <p>Zowel de ruwe als de base64-versie werkt; het formaat wordt automatisch herkend.</p>
    </details>
  </div>
</template>

<style scoped>
.settings {
  /*
   * A flex item in the app's 100dvh column: without flex/min-height/overflow it
   * would be squashed rather than scrolled once the content grows.
   */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
  max-width: 34rem;
  margin: 0 auto;
  padding: 24px 16px 40px;
}

.settings h1 {
  font-size: 20px;
  margin: 0 0 8px;
}

.settings__lead {
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.55;
  margin: 0 0 20px;
}

.settings__form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 0;
}

.settings__form label {
  font-size: 13px;
  color: var(--text-muted);
}

.settings__form button {
  align-self: flex-start;
}

.settings__help {
  margin-top: 28px;
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.6;
}

.settings__help summary {
  cursor: pointer;
  color: var(--accent);
}

.settings__help ol {
  margin: 10px 0;
  padding-left: 20px;
}
</style>
